import React, { useState } from 'react';
import { 
  ExternalLink, 
  Copy, 
  Check, 
  Cpu, 
  Shirt, 
  Sparkles, 
  Box, 
  Database,
  HeartPulse,
  Printer,
  Paintbrush,
  Wrench,
  Package
} from 'lucide-react';
import { AppItem } from '../data/apps';

interface AppCardProps {
  app: AppItem;
}

export const AppCard: React.FC<AppCardProps> = ({ app }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = (e: React.MouseEvent) => {
    e.stopPropagation();
    navigator.clipboard.writeText(app.url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const getAccentStyles = (accent: AppItem['accent']) => {
    switch (accent) {
      case 'emerald':
        return {
          glow: 'group-hover:border-emerald-500/40 group-hover:shadow-emerald-500/10',
          iconBg: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20 group-hover:bg-emerald-500/20',
          badge: 'bg-emerald-500/10 text-emerald-300 border-emerald-500/30',
          btn: 'bg-emerald-600/20 hover:bg-emerald-600/30 text-emerald-300 border-emerald-500/30',
          status: 'bg-emerald-500',
        };
      case 'cyan':
        return {
          glow: 'group-hover:border-cyan-500/40 group-hover:shadow-cyan-500/10',
          iconBg: 'bg-cyan-500/10 text-cyan-400 border-cyan-500/20 group-hover:bg-cyan-500/20',
          badge: 'bg-cyan-500/10 text-cyan-300 border-cyan-500/30',
          btn: 'bg-cyan-600/20 hover:bg-cyan-600/30 text-cyan-300 border-cyan-500/30',
          status: 'bg-cyan-500',
        };
      case 'purple':
        return {
          glow: 'group-hover:border-purple-500/40 group-hover:shadow-purple-500/10',
          iconBg: 'bg-purple-500/10 text-purple-400 border-purple-500/20 group-hover:bg-purple-500/20',
          badge: 'bg-purple-500/10 text-purple-300 border-purple-500/30',
          btn: 'bg-purple-600/20 hover:bg-purple-600/30 text-purple-300 border-purple-500/30',
          status: 'bg-purple-500',
        };
      case 'amber':
        return {
          glow: 'group-hover:border-amber-500/40 group-hover:shadow-amber-500/10',
          iconBg: 'bg-amber-500/10 text-amber-400 border-amber-500/20 group-hover:bg-amber-500/20',
          badge: 'bg-amber-500/10 text-amber-300 border-amber-500/30',
          btn: 'bg-amber-600/20 hover:bg-amber-600/30 text-amber-300 border-amber-500/30',
          status: 'bg-amber-500',
        };
      case 'rose':
        return {
          glow: 'group-hover:border-rose-500/40 group-hover:shadow-rose-500/10',
          iconBg: 'bg-rose-500/10 text-rose-400 border-rose-500/20 group-hover:bg-rose-500/20',
          badge: 'bg-rose-500/10 text-rose-300 border-rose-500/30',
          btn: 'bg-rose-600/20 hover:bg-rose-600/30 text-rose-300 border-rose-500/30',
          status: 'bg-rose-500',
        };
      case 'indigo':
        return {
          glow: 'group-hover:border-indigo-500/40 group-hover:shadow-indigo-500/10',
          iconBg: 'bg-indigo-500/10 text-indigo-400 border-indigo-500/20 group-hover:bg-indigo-500/20',
          badge: 'bg-indigo-500/10 text-indigo-300 border-indigo-500/30',
          btn: 'bg-indigo-600/20 hover:bg-indigo-600/30 text-indigo-300 border-indigo-500/30',
          status: 'bg-indigo-500',
        };
      default:
        return {
          glow: 'group-hover:border-blue-500/40 group-hover:shadow-blue-500/10',
          iconBg: 'bg-blue-500/10 text-blue-400 border-blue-500/20 group-hover:bg-blue-500/20',
          badge: 'bg-blue-500/10 text-blue-300 border-blue-500/30',
          btn: 'bg-blue-600/20 hover:bg-blue-600/30 text-blue-300 border-blue-500/30',
          status: 'bg-blue-500',
        };
    }
  };

  const styles = getAccentStyles(app.accent);

  const renderIcon = () => {
    switch (app.icon) {
      case 'dog':
        return <HeartPulse className="w-6 h-6" />;
      case 'cpu':
        return <Cpu className="w-6 h-6" />;
      case 'shirt':
        return <Shirt className="w-6 h-6" />;
      case 'sparkles':
        return <Sparkles className="w-6 h-6" />;
      case 'database':
        return <Database className="w-6 h-6" />;
      case 'printer':
        return <Printer className="w-6 h-6" />;
      case 'paintbrush':
        return <Paintbrush className="w-6 h-6" />;
      case 'wrench':
        return <Wrench className="w-6 h-6" />;
      case 'package':
        return <Package className="w-6 h-6" />;
      default:
        return <Box className="w-6 h-6" />;
    }
  };

  return (
    <div
      className={`group relative flex flex-col justify-between rounded-2xl bg-slate-900/60 border border-slate-800/80 p-6 backdrop-blur-md shadow-xl transition-all duration-300 hover:-translate-y-1 ${styles.glow}`}
    >
      {/* Top Header */}
      <div>
        <div className="flex items-start justify-between gap-4 mb-4">
          {/* Icon */}
          <div
            className={`w-12 h-12 rounded-xl border flex items-center justify-center transition-transform group-hover:scale-110 duration-200 ${styles.iconBg}`}
          >
            {renderIcon()}
          </div>

          {/* Badges */}
          <div className="flex items-center gap-2">
            <span
              className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium border ${styles.badge}`}
            >
              <span className={`w-1.5 h-1.5 rounded-full ${styles.status} animate-pulse`} />
              {app.badge}
            </span>
          </div>
        </div>

        {/* Title */}
        <h3 className="text-lg font-bold text-white group-hover:text-tiger-400 transition-colors">
          {app.title}
        </h3>

        {/* Subdomain pill + Copy */}
        <div className="flex items-center gap-2 mt-1.5 mb-3">
          <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-slate-950/70 border border-slate-800 text-xs font-mono text-slate-300">
            <span>{app.subdomain}</span>
          </div>
          <button
            onClick={handleCopy}
            title="Copy full URL"
            className="p-1 rounded-md text-slate-400 hover:text-white hover:bg-slate-800 transition-colors"
          >
            {copied ? (
              <Check className="w-3.5 h-3.5 text-emerald-400" />
            ) : (
              <Copy className="w-3.5 h-3.5" />
            )}
          </button>
        </div>

        {/* Description */}
        <p className="text-sm text-slate-400 leading-relaxed line-clamp-3">
          {app.description}
        </p>

        {/* Tags */}
        <div className="flex flex-wrap gap-1.5 mt-4">
          {app.tags.map((tag) => (
            <span
              key={tag}
              className="text-[11px] font-medium px-2 py-0.5 rounded bg-slate-800/60 text-slate-400 border border-slate-700/40"
            >
              #{tag}
            </span>
          ))}
        </div>
      </div>

      {/* Card Footer: Action Button */}
      <div className="mt-6 pt-4 border-t border-slate-800/60 flex items-center justify-between">
        <span className="text-xs font-medium text-slate-400">
          {app.category}
        </span>

        <a
          href={app.url}
          target="_blank"
          rel="noopener noreferrer"
          className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-semibold border transition-all duration-200 hover:scale-[1.03] active:scale-[0.98] ${styles.btn}`}
        >
          <span>Launch App</span>
          <ExternalLink className="w-3.5 h-3.5" />
        </a>
      </div>
    </div>
  );
};
